import React, { useEffect, useState } from 'react';
import { Link, Outlet, useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Circle, CircleDot, ArrowLeft } from 'lucide-react';
import { useFormatDate } from '../../hooks/useFormatDate';
import { usePublicBookingStore } from '../../store/publicBooking';
import { AcademyBrandingProvider } from '../../contexts/AcademyBrandingContext';
import { Card, CardContent, CardDescription, CardHeader } from '../../components/ui/Card';
import { Button } from '@/components/ui';
import { PublicBookingLessonCard } from './publicBooking/PublicBookingLessonCard';
import { PublicOrgSubpageHero } from '@/components/public/PublicOrgSubpageHero';
import { OrgSignInLink } from '@/components/public/org/OrgSignInLink';
import { publicBookingReturnUrlForLogin } from './publicBooking/bookingPaths';
import { isGroupDropInPublicBooking } from './publicBooking/publicBookingSessionMode';
import { resolveGroupDropInSessionDate } from './publicBooking/resolveGroupDropInSessionDate';
import { lessonService } from '../../services/lesson';
import type { Academy, Lesson, ProRataPreviewResponse, Term } from '@court-sync/shared';
import { STUDENT_INVITE_QUERY_PARAM } from '@court-sync/shared';
import { buildAcademyPath } from '../../lib/academyPaths';
import { useAcademySlug } from '../../hooks/useAcademySlug';
import { useAcademyPaths } from '../../hooks/useAcademyPaths';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { usePublicOrgProfile } from '@/queries/publicOrgProfile';
import { isStarterRequestBookingAcademy } from '../../utils/publicBrowseMode';
import { StarterMemberAuthNotFound } from '../../components/public/StarterMemberAuthNotFound';

export interface PublicBookingPageProps {
  /** Render inside {@link PublicOrgLayout} on unified club-academy routes (no duplicate nav/branding). */
  suppressPublicChrome?: boolean;
}

function PublicBookingLessonHero({
  academy,
  lesson,
  term,
}: {
  academy: Academy;
  lesson: Lesson;
  term: Term | null;
}) {
  const [bannerError, setBannerError] = useState(false);
  const hasBanner = !!(academy.bannerUrl && !bannerError);

  return (
    <section className="relative isolate overflow-hidden" aria-labelledby="public-booking-hero-heading">
      <div className="absolute inset-0">
        {hasBanner ? (
          <img
            src={academy.bannerUrl}
            alt=""
            className="h-full w-full min-h-[200px] object-cover object-center md:min-h-[240px]"
            referrerPolicy="no-referrer"
            onError={() => setBannerError(true)}
          />
        ) : (
          <div
            className="h-full min-h-[200px] w-full bg-gradient-to-br from-primary/25 via-primary/10 to-muted/40 md:min-h-[240px]"
            aria-hidden
          />
        )}
      </div>
      {hasBanner && <div className="absolute inset-0 bg-black/10" aria-hidden />}
      <div className="relative z-10 mx-auto max-w-screen-2xl px-4 pb-10 pt-8 sm:px-6 sm:pb-12 sm:pt-10 lg:px-8">
        <div
          className={
            hasBanner
              ? 'max-w-3xl rounded-xl bg-background/95 p-6 shadow-sm backdrop-blur-sm sm:p-8'
              : 'max-w-3xl'
          }
        >
          <Link
            to={buildAcademyPath(academy, '')}
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            ← Academy home
          </Link>
          <h1
            id="public-booking-hero-heading"
            className="mt-4 max-w-3xl text-3xl font-bold tracking-tight text-foreground md:text-4xl"
          >
            {lesson.name}
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            {term?.name ? `${term.name} · ${academy.name}` : `Book with ${academy.name}`}
          </p>
        </div>
      </div>
    </section>
  );
}

function BookingWizardBody({
  academy,
  lesson,
  term,
  lessonCoaches,
  formatDate,
  selectedSessionDates,
  proRataPreviewForCard,
  isLessonRegisterStep,
  academySlug,
  lessonId,
  bookingLoginReturnUrl,
}: {
  academy: Academy;
  lesson: Lesson;
  term: Term | null;
  lessonCoaches: ReturnType<typeof usePublicBookingStore.getState>['lessonCoaches'];
  formatDate: ReturnType<typeof useFormatDate>['formatDate'];
  selectedSessionDates: string[];
  proRataPreviewForCard: ProRataPreviewResponse | null;
  isLessonRegisterStep: boolean;
  academySlug: string | undefined;
  lessonId: string | undefined;
  bookingLoginReturnUrl: string | null;
}) {
  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start xl:gap-10">
      <div className="min-w-0">
        <PublicBookingLessonCard
          lesson={lesson}
          academy={academy}
          term={term}
          lessonCoaches={lessonCoaches}
          formatDate={formatDate}
          selectedSessionDates={selectedSessionDates}
          proRataPreview={proRataPreviewForCard}
        />
      </div>

      <div className="min-w-0">
        <Card className="overflow-hidden border-border/80 bg-card shadow-sm">
          <CardHeader className="border-b border-border bg-muted/20 pb-5">
            <h2 className="text-xl font-semibold tracking-tight text-foreground">Continue your booking</h2>
            {isLessonRegisterStep && academySlug && lessonId ? (
              <CardDescription>
                You need an account for this academy to book. Already have one?{' '}
                <OrgSignInLink returnUrl={bookingLoginReturnUrl ?? undefined}>Sign in</OrgSignInLink>.
              </CardDescription>
            ) : null}
          </CardHeader>
          <CardContent className="pt-6">
            <Outlet />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function PublicBookingPage({
  suppressPublicChrome: suppressPublicChromeProp = false,
}: PublicBookingPageProps = {}) {
  const suppressPublicChrome = suppressPublicChromeProp === true;
  const academySlug = useAcademySlug();
  const orgSlug = useOrgSlug();
  const { lessonId } = useParams<{ lessonId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const academy = usePublicBookingStore((s) => s.academy);
  const { formatDate } = useFormatDate({ region: academy?.region, publicContext: true });
  const { host: orgHost, loading: orgHostLoading } = useOrgHost();
  const { profile: orgProfile } = usePublicOrgProfile({
    slug: orgSlug,
    subdomainLabel: orgHost.mode === 'subdomain' ? orgHost.subdomainLabel : undefined,
    hostLoading: orgHost.mode === 'subdomain' && orgHostLoading,
    enabled:
      suppressPublicChrome &&
      (!!orgSlug || (orgHost.mode === 'subdomain' && !!orgHost.subdomainLabel)),
  });
  const { p: orgPath } = useOrgPaths(orgProfile?.organization);

  const isLessonRegisterStep = /\/book\/[^/]+\/register\/?$/.test(location.pathname);
  const invitationToken = usePublicBookingStore((s) => s.invitationToken);
  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const waitlistClaimEntryId = usePublicBookingStore((s) => s.waitlistClaimEntryId);

  const initialize = usePublicBookingStore((s) => s.initialize);
  const { p } = useAcademyPaths(academy);
  const lesson = usePublicBookingStore((s) => s.lesson);
  const term = usePublicBookingStore((s) => s.term);
  const lessonCoaches = usePublicBookingStore((s) => s.lessonCoaches);
  const selectedSessionDates = usePublicBookingStore((s) => s.selectedSessionDates);
  const setSelectedSessionDates = usePublicBookingStore((s) => s.setSelectedSessionDates);
  const groupBookingMode = usePublicBookingStore((s) => s.groupBookingMode);
  const lessonOverrides = usePublicBookingStore((s) => s.lessonOverrides);
  const isDataLoading = usePublicBookingStore((s) => s.isDataLoading);
  const dataError = usePublicBookingStore((s) => s.dataError);

  const [proRataPreviewForLesson, setProRataPreviewForLesson] = useState<{
    lessonId: string;
    preview: ProRataPreviewResponse;
  } | null>(null);

  const searchKey = searchParams.toString();

  const shouldLoadProRata =
    !!academy?.proRataEnabled &&
    !!academy?.id &&
    !!lesson?.termId &&
    lesson.lessonType === 'group';

  useEffect(() => {
    if (academySlug && lessonId) {
      void initialize(academySlug, lessonId, searchParams);
    }
  }, [academySlug, lessonId, initialize, searchKey, searchParams]);

  useEffect(() => {
    if (isDataLoading || !lesson) return;
    if (!isGroupDropInPublicBooking(lesson, groupBookingMode)) return;
    const next = resolveGroupDropInSessionDate(lesson, term, [], lessonOverrides);
    setSelectedSessionDates(next ? [next] : []);
  }, [lesson, term, groupBookingMode, lessonOverrides, isDataLoading, setSelectedSessionDates]);

  useEffect(() => {
    if (!academySlug || !lessonId || isDataLoading) return;
    const t = manualInviteToken?.trim();
    if (!t) return;
    const inUrl = searchParams.get(STUDENT_INVITE_QUERY_PARAM)?.trim();
    if (inUrl === t) return;
    const next = new URLSearchParams(searchParams);
    next.set(STUDENT_INVITE_QUERY_PARAM, t);
    navigate(`${location.pathname}?${next.toString()}${location.hash}`, { replace: true });
  }, [
    academySlug,
    lessonId,
    isDataLoading,
    manualInviteToken,
    searchParams,
    navigate,
    location.pathname,
    location.hash,
  ]);

  useEffect(() => {
    if (!shouldLoadProRata || !lesson || !academy) {
      return undefined;
    }
    let cancelled = false;
    const lid = lesson.id;
    void lessonService
      .getProRataPreview(lid, academy.id)
      .then((preview) => {
        if (!cancelled) setProRataPreviewForLesson({ lessonId: lid, preview });
      })
      .catch(() => {
        if (!cancelled) setProRataPreviewForLesson(null);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldLoadProRata, lesson?.id, academy?.id]);

  const proRataPreviewForCard =
    shouldLoadProRata &&
    lesson &&
    proRataPreviewForLesson?.lessonId === lesson.id
      ? proRataPreviewForLesson.preview
      : null;

  const homePath = suppressPublicChrome ? (orgPath?.('/lessons') ?? '/') : (p?.('') ?? '/');

  const loadingShell = (
    <div
      className={
        suppressPublicChrome
          ? 'flex min-h-[320px] items-center justify-center p-6'
          : 'flex min-h-screen items-center justify-center bg-background'
      }
    >
      <div className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
        <p className="text-muted-foreground">Loading lesson...</p>
      </div>
    </div>
  );

  const errorShell = (
    <div
      className={
        suppressPublicChrome
          ? 'flex min-h-[320px] flex-col items-center justify-center p-6 text-center'
          : 'flex min-h-screen items-center justify-center bg-background'
      }
    >
      <div className="text-center">
        <div className="mb-4 flex justify-center">
          <Circle className="h-16 w-16 text-muted-foreground" />
        </div>
        <h1 className="mb-2 text-2xl font-bold text-foreground">Lesson Not Found</h1>
        <p className="mb-4 text-muted-foreground">
          {dataError || 'The lesson you are looking for does not exist.'}
        </p>
        <Button onClick={() => navigate(homePath)}>
          {suppressPublicChrome ? 'Back to lessons' : 'Back to Academy'}
        </Button>
      </div>
    </div>
  );

  if (isDataLoading) {
    return loadingShell;
  }

  if (dataError || !academy || !lesson) {
    return errorShell;
  }

  if (isStarterRequestBookingAcademy(academy)) {
    return <StarterMemberAuthNotFound academy={academy} academySlug={academySlug} />;
  }

  const wizardBody = (
    <BookingWizardBody
      academy={academy}
      lesson={lesson}
      term={term}
      lessonCoaches={lessonCoaches}
      formatDate={formatDate}
      selectedSessionDates={selectedSessionDates}
      proRataPreviewForCard={proRataPreviewForCard}
      isLessonRegisterStep={isLessonRegisterStep}
      academySlug={academySlug}
      lessonId={lessonId}
      bookingLoginReturnUrl={
        academySlug && lessonId
          ? publicBookingReturnUrlForLogin(academySlug, lessonId, {
              invitationToken,
              manualInviteToken,
              waitlistEntryId: waitlistClaimEntryId,
            })
          : null
      }
    />
  );

  if (suppressPublicChrome) {
    const bannerUrl = orgProfile?.organization.bannerUrl ?? academy.bannerUrl;
    const lessonsBackPath = orgPath?.('/lessons');

    return (
      <>
        <PublicOrgSubpageHero
          bannerUrl={bannerUrl}
          title={lesson.name}
          headingId="public-booking-hero-heading"
          backLink={
            lessonsBackPath
              ? { to: lessonsBackPath, label: 'Back to lessons' }
              : undefined
          }
        >
          <p className="mt-3 max-w-2xl text-sm text-white/85 sm:text-base">
            {term?.name ? `${term.name} · ${academy.name}` : `Book with ${academy.name}`}
          </p>
        </PublicOrgSubpageHero>

        <div className="bg-muted/30">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-10">{wizardBody}</div>
        </div>
      </>
    );
  }

  return (
    <AcademyBrandingProvider primaryColor={academy.primaryColor} secondaryColor={academy.secondaryColor}>
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border bg-card shadow-sm">
          <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 justify-between">
              <div className="flex items-center">
                {academy.logoUrl ? (
                  <img
                    src={academy.logoUrl}
                    alt={academy.name}
                    className="h-10 w-auto object-contain"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <>
                    <div className="rounded-lg bg-gradient-to-r from-primary-600 to-tennis-600 p-2">
                      <CircleDot className="h-5 w-5 text-white" />
                    </div>
                    <span className="ml-2 text-xl font-bold gradient-text">{academy.name}</span>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate(p?.('') ?? '/')}
                  className="flex items-center"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Academy
                </Button>
              </div>
            </div>
          </div>
        </nav>

        <PublicBookingLessonHero key={lessonId} academy={academy} lesson={lesson} term={term} />

        <div className="mx-auto max-w-screen-2xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
          {wizardBody}
        </div>
      </div>
    </AcademyBrandingProvider>
  );
}
